import { useState, useRef, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

export interface ItemNote {
  id: number;
  content: string;
}

interface EditingCell {
  id: number;
  value: string;
}

interface EditableItemListProps {
  items: ItemNote[];
  onUpdate: (id: number, value: string) => void;
  onDelete: (id: number) => void;
  onAdd: () => void;
  readOnly?: boolean;
  pdfMode?: boolean;
  title?: string;
}

export function EditableItemList({
  items,
  onUpdate,
  onDelete,
  onAdd,
  readOnly = false,
  pdfMode = false,
  title = "Notes"
}: EditableItemListProps) {
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [pendingNewItemEdit, setPendingNewItemEdit] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (pendingNewItemEdit && items.length > 0) {
      const newItem = items[items.length - 1];
      if (newItem) {
        startEditing(newItem.id, newItem.content || '');
        setPendingNewItemEdit(false);
      }
    }
  }, [pendingNewItemEdit, items]);
  
  const startEditing = (id: number, value: any) => {
    if (readOnly) return;
    setEditingCell({
      id,
      value: String(value)
    });
    setEditValue(String(value));
  };
  
  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue("");
  };
  
  const handleSave = (id: number, value: string) => {
    onUpdate(id, value);
    cancelEditing();
  };

  const handleAddItem = () => {
    onAdd();
    setPendingNewItemEdit(true);
  };
  
  // Handle tab navigation between items
  const handleTabNavigation = (e: React.KeyboardEvent<HTMLInputElement>, currentId: number) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      
      // Save the current cell first
      handleSave(currentId, editValue);
      
      // Find the current item index
      const currentItemIndex = items.findIndex(item => item.id === currentId);
      
      // If there's a next item, start editing it
      if (currentItemIndex < items.length - 1) {
        const nextItemId = items[currentItemIndex + 1].id;
        setTimeout(() => startEditing(nextItemId, items[currentItemIndex + 1].content), 10);
      } else {
        // If we're at the last item, add a new one
        onAdd();
        setPendingNewItemEdit(true);
      }
    }
  };

  const displayItems = pdfMode ? 
    items.filter(item => item.content && item.content.trim() !== '') : 
    items;

  return (
    <div className="relative">
      <Table className="text-sm">
        <TableHeader>
          <TableRow className="bg-gray-100">
            <TableHead className="py-1 px-4 text-left">{title}</TableHead>
            {!readOnly && !pdfMode && <TableHead className="w-10"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayItems.map(item => (
            <TableRow key={item.id} className="border-b">
              <TableCell className="py-2 px-4">
                {editingCell?.id === item.id ? (
                  <Input 
                    ref={inputRef} 
                    value={editValue} 
                    onChange={e => setEditValue(e.target.value)} 
                    className="h-7 w-full" 
                    onKeyDown={e => {
                      if (e.key === "Enter") handleSave(item.id, editValue);
                      if (e.key === "Escape") cancelEditing();
                      // Add the tab navigation handler
                      handleTabNavigation(e, item.id);
                    }}
                    onBlur={() => handleSave(item.id, editValue)} 
                    autoFocus 
                  />
                ) : (
                  <div 
                    className={`${!readOnly && !pdfMode ? "cursor-pointer hover:bg-accent hover:text-accent-foreground" : ""} p-2 rounded min-h-[2rem] flex items-center`} 
                    onClick={() => !pdfMode && startEditing(item.id, item.content)}
                  >
                    {item.content ? item.content : <span className="text-gray-400 italic">Click to add text</span>}
                  </div>
                )}
              </TableCell>
              {!readOnly && !pdfMode && (
                <TableCell className="p-0 w-10">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => onDelete(item.id)} 
                    className="h-7 w-7 pdf-exclude"
                    disabled={items.length <= 1}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {!readOnly && !pdfMode && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleAddItem} 
          className="text-gray-500 hover:text-primary mt-2 h-7 pl-4 pdf-exclude"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Item
        </Button>
      )}
    </div>
  );
}
